import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSpinner, faArrowLeft, faBan, faUserPlus, faFolderOpen, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { apiJson } from '../apiClient';
import { PageHeader } from '../../crm/components/ui/PageHeader';
import { Panel } from '../../crm/components/ui/Panel';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Badge, StatusBadge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Tabs } from '../../crm/components/ui/Tabs';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';
import { SegmentedControl } from '../../crm/components/ui/SegmentedControl';
import { AssinaturaSheet } from '../../crm/components/financeiro/AssinaturaSheet';
import { LancamentoSheet } from '../../crm/components/financeiro/LancamentoSheet';
import { MonthPicker } from '../../crm/components/ui/MonthPicker';
import { PeriodFilterBar } from '../../crm/components/financeiro/PeriodFilterBar';

const apiCli = (path, opts) => apiJson(`/clientes${path}`, opts);

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');
const mesAtual = () => new Date().toISOString().slice(0, 7);
const mesAnterior = (competencia) => {
  const [a, m] = competencia.split('-').map(Number);
  const d = new Date(a, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nomeCompetencia = (c) => {
  if (!c) return '—';
  const [a, m] = c.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

// ─── Lista ───────────────────────────────────────────────────────────────
function ListaClientes({ onAbrirCliente, showToast }) {
  const [clientes, setClientes] = useState(null);
  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('ativo');
  const [emAtrasoFiltro, setEmAtrasoFiltro] = useState(false);
  const [showNovo, setShowNovo] = useState(false);
  const [nome, setNome] = useState('');
  const [documento, setDocumento] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [sinais, setSinais] = useState({});

  const carregar = useCallback(async () => {
    const params = new URLSearchParams();
    if (busca) params.set('nome', busca);
    if (statusFiltro) params.set('status', statusFiltro);
    const r = await apiCli(`?${params}`);
    if (r.status === 'ok') setClientes(r.lista);
  }, [busca, statusFiltro]);

  useEffect(() => { carregar(); }, [carregar]);
  // Item 11 do diagnóstico de usabilidade: sinal de "cliente esfriando"
  // (regra fixa — atraso recorrente / faturamento em queda), calculado sob
  // demanda ao abrir a lista.
  useEffect(() => { apiJson('/financeiro/clientes/sinais').then((r) => { if (r.status === 'ok') setSinais(r.sinais); }); }, []);

  const salvar = async () => {
    if (!nome.trim()) { showToast?.('Informe o nome', 'error'); return; }
    setSalvando(true);
    const r = await apiCli('', { method: 'POST', body: JSON.stringify({ nome, documento: documento || undefined }) });
    setSalvando(false);
    if (r.status === 'ok') { showToast?.('Cliente criado!', 'success'); setShowNovo(false); setNome(''); setDocumento(''); carregar(); }
    else showToast?.(r.mensagem || 'Erro ao criar cliente', 'error');
  };

  const listaFiltrada = (clientes || []).filter((c) => !emAtrasoFiltro || sinais[c._id]?.em_atraso);

  return (
    <div>
      <PageHeader
        icon={faUserPlus}
        title="Clientes"
        subtitle="Relacionamento, contratos e histórico de cada cliente."
        actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Cliente</Button>}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-4)' }}>
        <div className="crm-search-input" style={{ minWidth: '260px' }}>
          <FontAwesomeIcon icon={faSearch} />
          <input className="crm-input" placeholder="Buscar cliente..." value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <select className="crm-select" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} style={{ maxWidth: '150px' }}>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
          <option value="">Todos</option>
        </select>
        <SegmentedControl
          size="sm"
          value={emAtrasoFiltro}
          onChange={setEmAtrasoFiltro}
          options={[{ value: false, label: 'Todos' }, { value: true, label: 'Em atraso' }]}
        />
      </div>

      <Panel flush>
        {clientes === null ? (
          <EmptyState title="Carregando..." />
        ) : listaFiltrada.length === 0 ? (
          <EmptyState
            title={clientes.length === 0 ? 'Nenhum cliente cadastrado' : 'Nenhum cliente nesse filtro'}
            description={clientes.length === 0 ? 'Cadastre seu primeiro cliente para começar a organizar projetos, financeiro e equipamentos.' : undefined}
            actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Cliente</Button>}
          />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Nome</th><th>Documento</th><th>Status</th><th>Sinal</th></tr></thead>
              <tbody>
                {listaFiltrada.map((c) => (
                  <tr key={c._id} className="clickable" onClick={() => onAbrirCliente(c._id)}>
                    <td className="crm-table-primary">{c.nome}</td>
                    <td>{c.documento || '—'}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td style={{ display: 'flex', gap: 'var(--crm-space-1)', flexWrap: 'wrap' }}>
                      {sinais[c._id]?.atraso_recorrente && <Badge tone="danger">Atraso recorrente</Badge>}
                      {sinais[c._id]?.faturamento_em_queda && <Badge tone="warning">Faturamento em queda</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet
        open={showNovo}
        onClose={() => setShowNovo(false)}
        title="Novo Cliente"
        footer={<Button variant="primary" loading={salvando} onClick={salvar}>Criar Cliente</Button>}
      >
        <div className="crm-form-field">
          <label className="crm-form-label">Nome</label>
          <input className="crm-input" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Documento (CNPJ/CPF, opcional)</label>
          <input className="crm-input" value={documento} onChange={(e) => setDocumento(e.target.value)} />
        </div>
      </Sheet>
    </div>
  );
}

// ─── Aba Visão Geral ───────────────────────────────────────────────────────
function AbaVisaoGeral({ cliente, resumo, clienteId, showToast, onContatoAdicionado }) {
  const [atividade, setAtividade] = useState(null);
  const [showContato, setShowContato] = useState(false);
  const [nomeContato, setNomeContato] = useState('');
  const [cargoContato, setCargoContato] = useState('');
  const [telefoneContato, setTelefoneContato] = useState('');
  const [emailContato, setEmailContato] = useState('');
  const [principalContato, setPrincipalContato] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    apiJson(`/auditoria?client_id=${clienteId}&limite=8`).then((r) => { if (r.status === 'ok') setAtividade(r.lista); });
  }, [clienteId]);

  const cards = [
    { label: 'MRR', valor: resumo.financeiro ? fmtBRL(resumo.financeiro.mrr) : '—' },
    { label: 'A receber', valor: resumo.financeiro ? fmtBRL(resumo.financeiro.a_receber) : '—' },
    { label: 'Vencido', valor: resumo.financeiro ? fmtBRL(resumo.financeiro.vencido) : '—', tone: resumo.financeiro && parseFloat(resumo.financeiro.vencido) > 0 ? 'danger' : undefined },
    { label: 'Projetos ativos', valor: resumo.projetos_ativos === null ? '—' : resumo.projetos_ativos },
    { label: 'Equipamentos', valor: resumo.equipamentos === null ? '—' : resumo.equipamentos },
    { label: 'Documentos', valor: resumo.documentos },
  ];

  const salvarContato = async () => {
    if (!nomeContato.trim()) { showToast?.('Informe o nome do contato', 'error'); return; }
    if (!telefoneContato.trim() && !emailContato.trim()) { showToast?.('Informe telefone ou e-mail', 'error'); return; }
    setSalvando(true);
    const contatos = [
      ...(principalContato ? (cliente.contatos || []).map((c) => ({ ...c, principal: false })) : (cliente.contatos || [])),
      { nome: nomeContato, cargo: cargoContato || undefined, telefone: telefoneContato || undefined, email: emailContato || undefined, principal: principalContato },
    ];
    const r = await apiCli(`/${clienteId}`, { method: 'PUT', body: JSON.stringify({ contatos }) });
    setSalvando(false);
    if (r.status === 'ok') {
      showToast?.('Contato adicionado!', 'success');
      setShowContato(false);
      setNomeContato(''); setCargoContato(''); setTelefoneContato(''); setEmailContato(''); setPrincipalContato(false);
      onContatoAdicionado();
    } else showToast?.(r.mensagem || 'Erro ao salvar contato', 'error');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <KpiGrid>
        {cards.map((c) => <KpiCard key={c.label} label={c.label} value={c.valor} tone={c.tone} />)}
      </KpiGrid>

      <Panel title="Contatos" actions={<Button size="sm" icon={faUserPlus} onClick={() => setShowContato(true)}>Contato</Button>}>
        {(cliente.contatos || []).length === 0 ? (
          <div className="crm-form-hint">Nenhum contato cadastrado.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
            {cliente.contatos.map((ct, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
                <strong>{ct.nome}</strong>
                {ct.cargo && <span className="crm-form-hint">({ct.cargo})</span>}
                {ct.principal && <Badge tone="accent">Principal</Badge>}
                <span className="crm-form-hint">{ct.telefone || ct.whatsapp || ct.email || 'sem contato'}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Atividade recente" flush>
        {atividade === null ? (
          <div style={{ padding: 'var(--crm-space-4)' }} className="crm-form-hint">Carregando...</div>
        ) : atividade.length === 0 ? (
          <EmptyState title="Nenhuma atividade ainda" />
        ) : (
          <div className="crm-timeline">
            {atividade.map((a) => (
              <div className="crm-timeline-item" key={a.id}>
                <span className="crm-timeline-desc">{a.description}</span>
                <span className="crm-timeline-when">{fmtDateTime(a.created_em)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Sheet
        open={showContato}
        onClose={() => setShowContato(false)}
        title="Novo contato"
        footer={<Button variant="primary" loading={salvando} onClick={salvarContato}>Adicionar</Button>}
      >
        <div className="crm-form-field">
          <label className="crm-form-label">Nome</label>
          <input className="crm-input" value={nomeContato} onChange={(e) => setNomeContato(e.target.value)} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Função (opcional — ex: Financeiro, Técnico)</label>
          <input className="crm-input" value={cargoContato} onChange={(e) => setCargoContato(e.target.value)} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Telefone/WhatsApp</label>
          <input className="crm-input" value={telefoneContato} onChange={(e) => setTelefoneContato(e.target.value)} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">E-mail</label>
          <input className="crm-input" value={emailContato} onChange={(e) => setEmailContato(e.target.value)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <input type="checkbox" checked={principalContato} onChange={(e) => setPrincipalContato(e.target.checked)} />
          Contato principal
        </label>
      </Sheet>
    </div>
  );
}

// ─── Aba Projetos (consulta project_clients, §8.1.1) ────────────────────
function AbaProjetos({ clienteId, onNavegar }) {
  const [projetos, setProjetos] = useState(null);

  useEffect(() => {
    apiJson(`/projetos?cliente_id=${clienteId}`).then((r) => { if (r.status === 'ok') setProjetos(r.lista); });
  }, [clienteId]);

  if (projetos === null) return <div className="crm-form-hint">Carregando...</div>;

  if (projetos.length === 0) {
    return (
      <EmptyState
        icon={faFolderOpen}
        title="Nenhum projeto associado a este cliente"
        description="Crie um projeto já vinculado a este cliente."
        actions={<Button variant="primary" icon={faPlus} onClick={() => onNavegar('projetos', { clienteIdParaNovoProjeto: clienteId })}>Projeto</Button>}
      />
    );
  }

  return (
    <Panel flush>
      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Projeto</th><th>Status</th><th>Tarefas</th></tr></thead>
          <tbody>
            {projetos.map((p) => (
              <tr key={p.id} className="clickable" onClick={() => onNavegar('projetos', { projetoId: p.id })}>
                <td className="crm-table-primary">{p.nome}</td>
                <td><StatusBadge status={p.status} /></td>
                <td>{p.tarefas_total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ─── Aba Financeiro (consulta Financeiro por client_id, §8.1.1) ─────────
const STATUS_ASSINATURA_TONE = { ATIVA: 'positive', PAUSADA: 'warning', CANCELADA: 'neutral' };

function AbaFinanceiro({ clienteId, showToast, onNavegar }) {
  const [receivables, setReceivables] = useState(null);
  const [assinaturas, setAssinaturas] = useState(null);
  const [assinaturaAberta, setAssinaturaAberta] = useState(null);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);

  // Item pedido pelo usuário: um único controle de período (mesmo
  // `PeriodFilterBar` reutilizado em toda tela financeira) no topo — KPIs
  // e tabelas abaixo seguem o mesmo período selecionado.
  const [periodo, setPeriodo] = useState({ de: mesAtual(), ate: mesAtual() });
  const periodoEhUmMes = periodo.de === periodo.ate;

  const [dash, setDash] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ de: periodo.de, ate: periodo.ate, cliente_id: clienteId });
    apiJson(`/financeiro/dashboard/executivo?${params}`).then((r) => { if (r.status === 'ok') setDash(r.dashboard); });
  }, [clienteId, periodo]);

  const carregarAssinaturas = useCallback(() => {
    const params = new URLSearchParams({ cliente_id: clienteId });
    if (periodoEhUmMes) params.set('competencia', periodo.de);
    apiJson(`/financeiro/recurrence-rules?${params}`).then((r) => { if (r.status === 'ok') setAssinaturas(r.lista); });
  }, [clienteId, periodoEhUmMes, periodo.de]);

  const carregarReceivables = useCallback(() => {
    const params = new URLSearchParams({ cliente_id: clienteId, competencia_de: periodo.de, competencia_ate: periodo.ate });
    if (mostrarArquivadas) params.set('incluir_arquivados', '1');
    apiJson(`/financeiro/receivables?${params}`).then((r) => { if (r.status === 'ok') setReceivables(r.lista); });
  }, [clienteId, periodo, mostrarArquivadas]);

  useEffect(() => { carregarReceivables(); carregarAssinaturas(); }, [carregarReceivables, carregarAssinaturas]);

  const avulsas = (receivables || []).filter((r) => r.tipo !== 'ASSINATURA');

  const cards = dash && [
    { label: 'MRR', valor: fmtBRL(dash.kpis.mrr_atual) },
    { label: 'Receita recebida', valor: fmtBRL(dash.resultado_acumulado.receita_recebida) },
    { label: 'A receber', valor: fmtBRL(dash.kpis.a_receber) },
    { label: 'Vencido', valor: fmtBRL(dash.kpis.vencido), tone: parseFloat(dash.kpis.vencido) > 0 ? 'danger' : undefined },
    { label: 'Despesas pagas', valor: fmtBRL(dash.resultado_acumulado.despesas_pagas) },
    { label: 'Resultado', valor: fmtBRL(dash.kpis.resultado), tone: parseFloat(dash.kpis.resultado) < 0 ? 'danger' : undefined },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <PeriodFilterBar onChange={(de, ate) => setPeriodo({ de, ate })} />

      {!dash ? (
        <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : (
        <KpiGrid>
          {cards.map((c) => (
            <div key={c.label}>
              <KpiCard label={c.label} value={c.valor} tone={c.tone} />
            </div>
          ))}
        </KpiGrid>
      )}

      {/* Timeline financeira (§14 da correção de UX do Financeiro): "o que
          já entrou, o que está em aberto, o que vem" numa linha só, em vez
          de precisar cruzar mentalmente as tabelas de baixo. */}
      {receivables && receivables.length > 0 && (
        <Panel title="Linha do tempo financeira" flush>
          <div className="crm-timeline">
            {[...receivables].sort((a, b) => (a.vencimento < b.vencimento ? 1 : -1)).slice(0, 8).map((r) => {
              const saldo = parseFloat(r.saldo ?? r.valor);
              const pago = saldo <= 0;
              return (
                <div key={r.id} className="crm-timeline-item clickable" onClick={() => setItemSelecionado({ ...r, _origem: 'receivable' })}>
                  <span className="crm-timeline-desc">
                    {pago ? <span style={{ color: 'var(--crm-positive)' }}>+{fmtBRL(r.valor)} recebido</span> : <span>{fmtBRL(saldo)} {r.situacao === 'VENCIDO' || r.situacao === 'PARCIAL_VENCIDO' ? 'vencido' : 'em aberto'}</span>} — {r.descricao}
                  </span>
                  <span className="crm-timeline-when">{fmtDate(r.vencimento)}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      <Panel title="Assinaturas" flush>
        {assinaturas === null ? (
          <div style={{ padding: 'var(--crm-space-4)' }} className="crm-form-hint">Carregando...</div>
        ) : assinaturas.length === 0 ? (
          <EmptyState title="Nenhuma assinatura para este cliente" />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Plano</th><th>Valor/mês</th><th>Vencimento</th><th>Assinatura</th>
                  {periodoEhUmMes && <th style={{ textTransform: 'capitalize' }}>{nomeCompetencia(periodo.de)}</th>}
                </tr>
              </thead>
              <tbody>
                {assinaturas.map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setAssinaturaAberta(a.id)}>
                    <td className="crm-table-primary">{a.descricao}</td>
                    <td>{fmtBRL(a.valor)}</td>
                    <td>dia {a.dia_vencimento}</td>
                    <td><Badge tone={STATUS_ASSINATURA_TONE[a.status]}>{a.status === 'ATIVA' ? 'Ativa' : a.status === 'PAUSADA' ? 'Pausada' : 'Cancelada'}</Badge></td>
                    {periodoEhUmMes && (
                      <td>
                        {a.competencia_gerada ? (
                          <StatusBadge status={a.competencia_status} />
                        ) : a.competencia_excecao_nao_gerada ? (
                          <Badge tone="warning">⚠ Não gerada</Badge>
                        ) : (
                          <span className="crm-form-hint">Próxima: {fmtDate(a.competencia_proxima_geracao)}</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Receitas avulsas"
        actions={
          <SegmentedControl
            size="sm"
            value={mostrarArquivadas}
            onChange={setMostrarArquivadas}
            options={[{ value: false, label: 'Ativas' }, { value: true, label: 'Todas' }]}
          />
        }
        flush
      >
        {receivables === null ? (
          <div style={{ padding: 'var(--crm-space-4)' }} className="crm-form-hint">Carregando...</div>
        ) : avulsas.length === 0 ? (
          <EmptyState title="Nenhuma receita avulsa para este cliente" />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Descrição</th><th>Competência</th><th>Vencimento</th><th>Status</th><th>Valor</th></tr></thead>
              <tbody>
                {avulsas.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setItemSelecionado({ ...r, _origem: 'receivable' })}>
                    <td className="crm-table-primary">{r.descricao}{r.arquivado_em && <span style={{ marginLeft: 'var(--crm-space-2)' }}><Badge tone="neutral">Arquivada</Badge></span>}</td>
                    <td style={{ textTransform: 'capitalize' }}>{nomeCompetencia(r.competencia)}</td>
                    <td>{fmtDate(r.vencimento)}</td>
                    <td><StatusBadge status={r.situacao} /></td>
                    <td>{fmtBRL(r.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <AssinaturaSheet
        assinaturaId={assinaturaAberta}
        open={!!assinaturaAberta}
        onClose={() => setAssinaturaAberta(null)}
        onChanged={carregarAssinaturas}
        showToast={showToast}
        onNavegar={onNavegar}
      />
      {itemSelecionado && (
        <LancamentoSheet
          item={itemSelecionado}
          onClose={() => setItemSelecionado(null)}
          onChanged={carregarReceivables}
          showToast={showToast}
          onNavegar={onNavegar}
          onAbrirAssinatura={setAssinaturaAberta}
        />
      )}
    </div>
  );
}

// ─── Aba Equipamentos (consulta Patrimônio por client_id, §8.1.1) ───────
function AbaEquipamentos({ clienteId, onNavegar }) {
  const [assets, setAssets] = useState(null);

  useEffect(() => {
    apiJson(`/patrimonio/assets?cliente_id=${clienteId}`).then((r) => { if (r.status === 'ok') setAssets(r.lista); });
  }, [clienteId]);

  if (assets === null) return <div className="crm-form-hint">Carregando...</div>;

  if (assets.length === 0) {
    return <EmptyState title="Nenhum equipamento com este cliente" description="Equipamentos entregues a este cliente aparecerão aqui." />;
  }

  return (
    <Panel flush>
      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Código</th><th>Equipamento</th><th>Status</th></tr></thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} className="clickable" onClick={() => onNavegar('patrimonio', { ativoId: a.id })}>
                <td className="crm-table-primary">{a.asset_tag}</td>
                <td>{a.produto_nome}</td>
                <td><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ─── Aba Documentos (consulta relações do domínio Documentos, §8.1.1) ───
function AbaDocumentos({ clienteId, clienteNome, onNavegar }) {
  const [documentos, setDocumentos] = useState(null);

  useEffect(() => {
    apiJson(`/documentos?entity_type=CLIENTE&entity_id=${clienteId}`).then((r) => { if (r.status === 'ok') setDocumentos(r.lista); });
  }, [clienteId]);

  if (documentos === null) return <div className="crm-form-hint">Carregando...</div>;

  if (documentos.length === 0) {
    return (
      <EmptyState
        title="Nenhum documento relacionado a este cliente"
        description="Contratos, propostas e comprovantes deste cliente aparecerão aqui."
        actions={<Button variant="primary" icon={faPlus} onClick={() => onNavegar('documentos', { relacaoDocumento: { entity_type: 'CLIENTE', entity_id: clienteId, nome: clienteNome } })}>Documento</Button>}
      />
    );
  }

  return (
    <Panel flush>
      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Nome</th><th>Tipo</th><th>Atualizado</th></tr></thead>
          <tbody>
            {documentos.map((d) => (
              <tr key={d.id} className="clickable" onClick={() => onNavegar('documentos', { documentoId: d.id })}>
                <td className="crm-table-primary">{d.titulo}</td>
                <td><Badge tone="accent">{d.tipo}</Badge></td>
                <td>{fmtDateTime(d.atualizado_em)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ─── Aba Histórico (auditoria central filtrada por cliente) ────────────
function AbaHistorico({ clienteId }) {
  const [logs, setLogs] = useState(null);
  const [limite, setLimite] = useState(50);

  const carregar = useCallback(async () => {
    const r = await apiJson(`/auditoria?client_id=${clienteId}&limite=${limite}`);
    if (r.status === 'ok') setLogs(r.lista);
  }, [clienteId, limite]);

  useEffect(() => { carregar(); }, [carregar]);

  if (logs === null) return <div className="crm-form-hint">Carregando...</div>;

  if (logs.length === 0) return <EmptyState title="Nenhuma atividade registrada ainda" />;

  return (
    <Panel flush>
      <div className="crm-timeline">
        {logs.map((log) => (
          <div className="crm-timeline-item" key={log.id}>
            <span className="crm-timeline-desc">{log.description}</span>
            <span className="crm-timeline-when">{fmtDateTime(log.created_em)}</span>
          </div>
        ))}
      </div>
      {logs.length >= limite && (
        <div style={{ padding: 'var(--crm-space-4)' }}>
          <Button variant="outline" onClick={() => setLimite(limite + 50)}>Carregar mais</Button>
        </div>
      )}
    </Panel>
  );
}

// ─── Detalhe (âncora de navegação — agrega Projetos/Financeiro/Patrimônio/
// Documentos consultando cada domínio por client_id, nunca duplicando
// dado — §8.1) ────────────────────────────────────────────────────────────
function DetalheCliente({ clienteId, onVoltar, showToast, onNavegar }) {
  const [resumo, setResumo] = useState(null);
  const [aba, setAba] = useState('geral');

  const carregar = useCallback(async () => {
    const r = await apiCli(`/${clienteId}/resumo`);
    if (r.status === 'ok') setResumo(r);
    else showToast?.(r.mensagem || 'Erro ao carregar cliente', 'error');
  }, [clienteId, showToast]);

  useEffect(() => { carregar(); }, [carregar]);

  const inativar = async () => {
    const r = await apiCli(`/${clienteId}`, { method: 'DELETE' });
    if (r.status === 'ok') { showToast?.('Cliente inativado.', 'success'); onVoltar(); }
    else showToast?.(r.mensagem || 'Erro ao inativar', 'error');
  };

  if (!resumo) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const { cliente, financeiro } = resumo;

  const tabs = [
    { id: 'geral', label: 'Visão Geral' },
    resumo.projetos_ativos !== null && { id: 'projetos', label: 'Projetos' },
    financeiro !== null && { id: 'financeiro', label: 'Financeiro' },
    resumo.equipamentos !== null && { id: 'equipamentos', label: 'Equipamentos' },
    { id: 'documentos', label: 'Documentos' },
    { id: 'historico', label: 'Histórico' },
  ].filter(Boolean);

  const acoes = [
    resumo.projetos_ativos !== null && { label: 'Projeto', onClick: () => onNavegar('projetos', { clienteIdParaNovoProjeto: clienteId }) },
    financeiro !== null && { label: 'Receita', onClick: () => onNavegar('financeiro', { clienteIdFinanceiro: clienteId, abrirCriacaoFinanceiro: 'RECEITA' }) },
    financeiro !== null && { label: 'Assinatura', onClick: () => onNavegar('financeiro', { clienteIdFinanceiro: clienteId, abrirCriacaoFinanceiro: 'ASSINATURA' }) },
    { label: 'Documento', onClick: () => onNavegar('documentos', { relacaoDocumento: { entity_type: 'CLIENTE', entity_id: clienteId, nome: cliente.nome } }) },
  ].filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--crm-space-4)', gap: 'var(--crm-space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)' }}>
          <button className="crm-icon-btn" onClick={onVoltar} aria-label="Voltar"><FontAwesomeIcon icon={faArrowLeft} /></button>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{cliente.nome}</h2>
            {cliente.documento && <div className="crm-form-hint">{cliente.documento}</div>}
          </div>
          <StatusBadge status={cliente.status} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          {acoes.map((a) => (
            <Button key={a.label} variant="outline" size="sm" icon={faPlus} onClick={a.onClick}>{a.label}</Button>
          ))}
          {cliente.status === 'ativo' && (
            <Button variant="danger" size="sm" icon={faBan} onClick={inativar}>Inativar</Button>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} atual={aba} onChange={setAba} />

      <div style={{ marginTop: 'var(--crm-space-4)' }}>
        {aba === 'geral' && (
          <AbaVisaoGeral cliente={cliente} resumo={resumo} clienteId={clienteId} showToast={showToast} onContatoAdicionado={carregar} />
        )}
        {aba === 'projetos' && <AbaProjetos clienteId={clienteId} onNavegar={onNavegar} />}
        {aba === 'financeiro' && financeiro !== null && <AbaFinanceiro clienteId={clienteId} showToast={showToast} onNavegar={onNavegar} />}
        {aba === 'equipamentos' && <AbaEquipamentos clienteId={clienteId} onNavegar={onNavegar} />}
        {aba === 'documentos' && <AbaDocumentos clienteId={clienteId} clienteNome={cliente.nome} onNavegar={onNavegar} />}
        {aba === 'historico' && <AbaHistorico clienteId={clienteId} />}
      </div>
    </div>
  );
}

// ─── Root ───────────────────────────────────────────────────────────────
function Clientes({ showToast, onNavegar, clienteIdInicial }) {
  const [clienteSelecionado, setClienteSelecionado] = useState(clienteIdInicial || null);
  if (clienteSelecionado) {
    return (
      <DetalheCliente
        clienteId={clienteSelecionado}
        onVoltar={() => setClienteSelecionado(null)}
        showToast={showToast}
        onNavegar={onNavegar || (() => {})}
      />
    );
  }
  return <ListaClientes onAbrirCliente={setClienteSelecionado} showToast={showToast} />;
}

export default Clientes;
